import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";

// Simulator detection - ONLY for iPhone 16 simulator (not Pro, not other simulators)
const isSimulator = () => {
  const userAgent = navigator.userAgent;

  // Ultra-specific detection for iPhone 16 simulator only
  // Check for localhost AND iOS 18.5 AND specific iPhone 16 patterns
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isIPhoneWithIOS18 = userAgent.includes('iPhone') && userAgent.includes('CPU iPhone OS 18_5');

  // iPhone 16 Pro simulator has different screen dimensions than iPhone 16
  // iPhone 16: 393 x 852 points
  // iPhone 16 Pro: 430 x 932 points
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  // iPhone 16 simulator has 393 x 852 screen dimensions
  const isIPhone16Dimensions = screenWidth === 393 && screenHeight === 852;

  // Additional check to exclude iPhone 16 Pro simulator (430 x 932)
  const isNotProDimensions = !(screenWidth === 430 && screenHeight === 932);

  const isIPhone16Simulator = isLocalhost && isIPhoneWithIOS18 && isIPhone16Dimensions && isNotProDimensions;



  // Only return true for iPhone 16 simulator specifically (not Pro, not Max)
  return isIPhone16Simulator;
};

// Hardcoded responses for simulator
const SIMULATOR_RESPONSES = {
  // This will be updated with user's actual name
  getBirthChart: (userName) => ({
    name: userName || "Simulator User",
    gender: "F",
    saju: {
      elements: [
        { element: "Wood", count: 3 },
        { element: "Fire", count: 2 },
        { element: "Earth", count: 1 },
        { element: "Metal", count: 1 },
        { element: "Water", count: 1 }
      ],
      dayMaster: "Wood",
      monthBranch: "Snake",
      yearBranch: "Tiger",
      hourBranch: "Dragon"
    },
    western: {
      planets: [
        { planet: "Sun", sign: "Libra", house: 1 },
        { planet: "Moon", sign: "Cancer", house: 10 }
      ]
    }
  }),

  getQuickRead: (userName, topic) => {
    const name = userName || "you";
    if (topic.toLowerCase().includes('love')) {
      return `${name}, your Wood day master with Fire elements shows you're naturally drawn to intensity, but your Earth deficiency means you might be avoiding the stability you actually crave. That person who gives you mixed signals? Your Fire is attracted to their unpredictability, but your missing Earth needs someone who shows up consistently. Stop romanticizing chaos.`;
    } else if (topic.toLowerCase().includes('work')) {
      return `${name}, your strong Wood energy means you're ambitious AF, but your low Earth element suggests you're avoiding the boring but necessary foundation work. You want the big wins without the daily grind. Your Fire elements give you charisma, but without Earth, you're all sparkle, no substance. Time to build something that lasts.`;
    } else {
      return `${name}, your Wood day master with scattered elements shows you're constantly adapting but never settling. That's not freedom—it's avoidance. Your Fire elements make you passionate about everything briefly, but your missing Earth means you never commit long enough to see real results. Stop running from stability.`;
    }
  },

  nudgeQuestion: "Want me to break down what your Four Pillars are really telling you about these patterns?",

  getExtendedNudge: (userName) => {
    const name = userName || "you";
    return `Listen ${name}, your Wood day master with that Snake month branch? Classic avoidant attachment. You're drawn to people who keep you guessing because it feels familiar. Your Fire elements make you think intensity equals connection, but your missing Earth means you're terrified of someone who actually shows up consistently. This isn't about the stars—it's about your childhood patterns playing out in your relationships. Time to name what you're really avoiding.`;
  },

  cardReading: {
    text: "Your Four Pillars show a Wood day master with scattered elements—you're constantly adapting but never committing. This week, someone from your past will reach out with familiar chaos. Your Fire elements will want to dive back in, but your missing Earth needs to ask: 'Am I choosing this person, or am I choosing my old patterns?' The cards say you'll have a moment of clarity. Don't ignore it.",
    card: "Breakfast_1.png"
  },

  getChatResponse: (userName) => {
    const name = userName || "you";
    return `I'm here to help ${name} see what your Four Pillars are really telling you. No woo-woo, just the truth about your patterns. What's really going on?`;
  },

  // Hardcoded chat responses to showcase conversation functionality
  getChatResponses: (userName) => {
    const name = userName || "you";
    return {
      // Common user inputs and Wooju's responses
      "i'm feeling anxious": `${name}, your Wood day master with low Earth elements? Classic anxiety pattern. You're constantly adapting but never grounding. Your Fire elements make you think you need to solve everything immediately, but your missing Earth needs you to slow down and build a foundation. What are you really avoiding by staying in this anxious state?`,

      "i keep dating the same type of person": `Listen ${name}, your Snake month branch with Wood day master? You're drawn to people who keep you guessing because it feels familiar. Your Fire elements mistake intensity for connection. But your missing Earth means you're terrified of someone who actually shows up consistently. This isn't about them—it's about your avoidant attachment. Time to name what you're really running from.`,

      "work is stressing me out": `${name}, your strong Wood energy means you're ambitious AF, but your low Earth element suggests you're avoiding the boring but necessary foundation work. You want the big wins without the daily grind. Your Fire elements give you charisma, but without Earth, you're all sparkle, no substance. What foundation are you avoiding building?`,

      "i don't know what i want": `${name}, your scattered elements show you're constantly adapting but never committing. That's not freedom—it's avoidance. Your Fire elements make you passionate about everything briefly, but your missing Earth means you never stick with anything long enough to see real results. Stop running from decisions. What are you afraid of choosing?`,

      "why do i keep self-sabotaging": `Your Wood day master with that Tiger year branch? Classic self-sabotage pattern. You're drawn to chaos because it feels familiar. Your Fire elements make you think you need drama to feel alive, but your missing Earth means you're terrified of stability. This isn't about the stars—it's about your childhood patterns. What familiar chaos are you recreating?`,

      "i feel stuck": `${name}, your Four Pillars show a Wood day master with scattered elements. You're not stuck—you're avoiding commitment. Your Fire elements make you think you need to feel inspired to take action, but your missing Earth means you're waiting for the perfect moment that never comes. Stop waiting for motivation. Start building something, anything.`,

      "default": `${name}, your Four Pillars are telling me something about this situation. What's really going on beneath the surface? I'm here to help you see the patterns you might be missing.`
    };
  }
};

// Maps a raw voice transcript to the canonical button value FlowController expects,
// based on which state the flow is currently in.
export function classifyVoiceIntent(transcript, currentInteraction, mode = SELF_MODE) {
  const t = (transcript || "").toLowerCase().trim();

  switch (currentInteraction) {
    case "quickRead": {
      if (mode !== SELF_MODE) {
        // PAIR_MODE: map speech to compatibility topic buttons
        if (/used to|ex |ex$|past|former|broke up|old/.test(t)) return "someone I used to love";
        if (/crush|like them|like her|like him|feelings for/.test(t)) return "someone I'm crushing on";
        return "someone I'm dating"; // default for PAIR_MODE
      }
      // SELF_MODE: map speech to personal topic buttons
      if (/love|relationship|connection|dating|partner|romance|someone/.test(t)) return "love & connections";
      if (/work|career|job|purpose|money|finance|business/.test(t)) return "work & purpose";
      return "just... life things";
    }
    case "readOtherPerson": {
      return /\b(yes|yeah|yep|yup|sure|definitely|absolutely|i do|have someone)\b/.test(t) ? "yes" : "no";
    }
    case "nudge": {
      if (/\b(yes|yeah|yep|yup|sure|definitely|absolutely|of course)\b/.test(t)) return "yes";
      if (/\b(maybe|kind of|sort of|perhaps|possibly|i guess|not sure)\b/.test(t)) return "maybe...";
      return "no";
    }
    case "extendedNudgeChoices": {
      return /show|ahead|reading|card|see|future|visual|what('s| is) (coming|next|ahead)/.test(t)
        ? "show me what's ahead"
        : "i just wanna chat";
    }
    default:
      return transcript;
  }
}

export const INTERACTION_TYPES = {
  INFO_CARD_SUBMIT: "infoCardSubmit",
  CHAT_CONTINUE: "chatContinue",
  FLOATING_BUTTONS: "floatingButtons",
  OPEN_APP: "openApp",
};

export const SCREENS = {
  WOOJU_MACHINE: "woojuMachine",
  INFO_CARD: "infoCard",
  CHAT: "chat",
  CARD_READING: "cardReading",
};

/**
 * @typedef {Object} RequestData
 * @property {string} interactionType - The type of interaction (from INTERACTION_TYPES)
 * @property {Object} data - The data for the interaction.
 */

/**
 * @typedef {Object} ScreenSpecification
 * @property {string} screenName
 * @property {string[]} interactionTypes - Allowed interaction types for this screen.
 * @property {Object} woojuMachine
 * @property {string} woojuMachine.bubbleText
 * @property {string[]} woojuMachine.floatingButtons
 * @property {Object} chat
 * @property {string} chat.text
 * @property {boolean} chat.allowResponse
 * @property {Object} cardReading
 * @property {string} cardReading.text
 * @property {string} cardReading.card
 */

const SELF_MODE = "self";
const PAIR_MODE = "pair";

// Groups all Cloud Function calls for one reading funnel into a single
// Langfuse session, independent of any Firestore chatSessions doc.
const generateSessionId = () => {
  const random = typeof crypto !== "undefined" && crypto.randomUUID ?
    crypto.randomUUID() :
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `session-${random}`;
};

export class FlowController {
  constructor() {
    this.state = {};
    this.mode = SELF_MODE;
    this.conversationHistory = [];
    this.userId = null;
    this.userMemory = null;
    this.personalizedWelcome = null;
    this.cardReadingText = null;
    this.noddingText = null;
    this.sessionId = generateSessionId();
    this.voiceMode = false;
  }

  setVoiceMode(enabled) {
    this.voiceMode = !!enabled;
  }

  // Wraps httpsCallable so every Cloud Function call carries the same
  // sessionId/userId, letting Langfuse group a user's full funnel into
  // one session regardless of which screen made the call. Also forwards
  // voiceMode so server-side prompts can adapt for spoken responses.
  callFunction(name, data = {}) {
    return httpsCallable(functions, name)({
      ...data,
      sessionId: this.sessionId,
      userId: data.userId ?? this.userId,
      voiceMode: data.voiceMode ?? this.voiceMode,
    });
  }

  setUserId(userId) {
    this.userId = userId;
    // Automatically load the latest profile data when user ID is set
    if (userId) {
      this.loadFromLocalStorage();
      // Prefetch user memory in the background while user fills InfoCard
      this._memoryPrefetchPromise = this._prefetchUserMemory(userId);
    }
  }

  async _synthesizeMemory() {
    try {
      if (!this.conversationHistory.length) return;
      const conversation = [
        this.chosenTopic ? `Topic: ${this.chosenTopic}` : null,
        this.quickReadResponse ? `Wooju: ${this.quickReadResponse}` : null,
        this.nudgeResponse ? `User: ${this.nudgeResponse}` : null,
        this.extendedNudgeResponse ? `Wooju: ${this.extendedNudgeResponse}` : null,
        ...this.conversationHistory.map(msg => `${msg.isUser ? "User" : "Wooju"}: ${msg.text}`),
      ].filter(Boolean).join("\n");

      console.log('[memory] synthesizing memory, conversation length:', this.conversationHistory.length);
      await this.callFunction("processConversation", { conversation });
      console.log('[memory] synthesis complete');
    } catch (e) {
      console.warn('[memory] synthesis failed:', e.message);
    }
  }

  async _prefetchUserMemory(userId) {
    try {
      console.log('[memory] prefetching user memory for:', userId);
      const memoryResult = await this.callFunction("getUserMemory", { userId });
      const { hasMemory, memory } = memoryResult.data;
      console.log('[memory] getUserMemory result — hasMemory:', hasMemory);
      if (hasMemory && memory) {
        this.userMemory = memory;
        const messageResult = await this.callFunction("generatePersonalizedFirstMessage", {
          userId,
          isRedFlagAnalysis: true,
          isKorean: false,
          userMemory: memory,
        });
        this.personalizedWelcome = messageResult.data?.personalizedMessage || null;
        console.log('[memory] personalized welcome ready:', this.personalizedWelcome);
      }
    } catch (e) {
      console.warn('[memory] prefetch failed:', e.message);
    }
  }

  reset() {
    // Reset the flow controller to initial state
    this.state = {};
    this.mode = SELF_MODE;
    this.conversationHistory = [];
    this.currentInteraction = null;
    this.chosenTopic = null;
    this.quickReadResponse = null;
    this.nudgeQuestion = null;
    this.nudgeResponse = null;
    this.extendedNudgeResponse = null;
    this.otherPersonIndex = null;
    this.sessionId = generateSessionId();

    // Reload the latest profile data after reset
    if (this.userId) {
      this.loadFromLocalStorage();
    }
  }

  saveToLocalStorage() {
    if (!this.userId) return;

    try {
      const flowState = {
        userInfo: this.state.userInfo,
        birthChart: this.state.birthChart,
        otherPeople: this.state.otherPeople || [],
        conversationHistory: this.conversationHistory || [],
        mode: this.mode,
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem('wooju_flow_state', JSON.stringify(flowState));
      console.log('Flow state saved to localStorage');
    } catch (error) {
      console.error('Error saving flow state to localStorage:', error);
    }
  }

  loadFromLocalStorage() {
    if (!this.userId) return;

    try {
      const savedFlowState = localStorage.getItem('wooju_flow_state');

      if (savedFlowState) {
        const flowState = JSON.parse(savedFlowState);

        // Load user info and birth chart
        if (flowState.userInfo) {
          this.state.userInfo = flowState.userInfo;
          this.state.birthChart = flowState.birthChart || flowState.userInfo;
        }

        // Load other flow state data
        this.state.otherPeople = flowState.otherPeople || [];
        this.conversationHistory = flowState.conversationHistory || [];
        this.mode = flowState.mode || SELF_MODE;

        console.log('Flow state loaded from localStorage');
      }
    } catch (error) {
      console.error('Error loading flow state from localStorage:', error);
    }
  }

  async generateNudgeQuestionScreen() {
    this.currentInteraction = "nudge";
    try {
      // Check if we have a pre-generated nudge question
      if (this.preGeneratedNudgeQuestion) {
        this.nudgeQuestion = this.preGeneratedNudgeQuestion;
        // Clear the cache since we're using it
        this.preGeneratedNudgeQuestion = null;
      } else {
        // TRUE PARALLELIZATION: Generate nudge question AND pre-generate extended nudge in parallel
        const [nudgeResult, extendedNudgeResult] = await Promise.all([
          this.callFunction("generateNudgeQuestion", {
            chosenTopic: this.chosenTopic,
            quickReadResponse: this.quickReadResponse,
            chatHistory: this.conversationHistory || [],
            isKorean: false,
          }),
          // Pre-generate extended nudge for common responses
          this.callFunction("generateExtendedNudge", {
            userChoice: "yes", // Most common response
            chosenTopic: this.chosenTopic,
            quickReadResponse: this.quickReadResponse,
            nudgeQuestion: "Want me to break down what your Four Pillars are really telling you about these patterns?",
            chatHistory: this.conversationHistory || [],
            birthChart: { person1: { ...this.state.birthChart, name: this.state.userInfo?.name, gender: this.state.userInfo?.gender } },
            isKorean: false,
          }).catch(() => null) // Don't fail if this fails
        ]);

        this.nudgeQuestion = nudgeResult.data.nudgeQuestion;

        // Cache the pre-generated extended nudge for faster response later
        if (extendedNudgeResult?.data?.extendedNudge) {
          this.preGeneratedExtendedNudge = extendedNudgeResult.data.extendedNudge;
        }
      }

      return {
        screenName: SCREENS.WOOJU_MACHINE,
        woojuMachine: {
          bubbleText: this.nudgeQuestion,
          floatingButtons: ["yes", "no", "maybe..."],
        },
      };
    } catch (error) {
      console.error("Failed to generate nudge question:", error);
      // Fallback: open chat with input enabled and no auto message
      this.currentInteraction = "chat";
      return {
        screenName: SCREENS.CHAT,
        chat: {
          text: "",
          allowResponse: true,
        },
      };
    }
  }

  async generateQuickReadScreen(chosenTopic) {
    this.chosenTopic = chosenTopic;

    // Check if running in simulator
    if (isSimulator()) {
      this.quickReadResponse = SIMULATOR_RESPONSES.getQuickRead(this.state.userInfo?.name, chosenTopic);
      this.currentInteraction = "postQuickRead";
      return {
        screenName: SCREENS.CHAT,
        chat: {
          text: this.quickReadResponse,
          allowResponse: false,
        },
      };
    }

    // PARALLELIZATION: Generate quick read AND pre-generate nudge question
    if (this.mode == SELF_MODE) {
      const [quickReadResult, nudgeQuestionResult] = await Promise.all([
        this.callFunction("quickReadChart", {
          birthChart: {
            person1: {
              ...this.state.birthChart,
              name: this.state.userInfo.name,
              gender: this.state.userInfo.gender,
            },
          },
          chosenTopic: this.chosenTopic,
          isRedFlagAnalysis: true,
          isKorean: false,
        }),
        // Pre-generate nudge question in parallel
        this.callFunction("generateNudgeQuestion", {
          chosenTopic: this.chosenTopic,
          quickReadResponse: "Loading...", // Placeholder
          chatHistory: this.conversationHistory || [],
          isKorean: false,
        }).catch(() => null) // Don't fail if this fails
      ]);

      this.quickReadResponse = quickReadResult.data.quickRead;

      // Cache pre-generated nudge question for faster response later
      if (nudgeQuestionResult?.data?.nudgeQuestion) {
        this.preGeneratedNudgeQuestion = nudgeQuestionResult.data.nudgeQuestion;
      }

      this.currentInteraction = "postQuickRead";
      return {
        screenName: SCREENS.CHAT,
        chat: {
          text: this.quickReadResponse,
          allowResponse: false,
        },
      };
    } else {
      // mode == PAIR_MODE - Parallel compatibility read + nudge question
      const [compatibilityResult, nudgeQuestionResult] = await Promise.all([
        this.callFunction("compatibilityQuickRead", {
          birthChart: {
            person1: {
              ...this.state.birthChart,
              name: this.state.userInfo.name,
              gender: this.state.userInfo.gender,
            },
            person2: this.state.otherPeople[this.otherPersonIndex],
          },
          chosenTopic: this.chosenTopic,
          isKorean: false,
        }),
        // Pre-generate nudge question in parallel
        this.callFunction("generateNudgeQuestion", {
          chosenTopic: this.chosenTopic,
          quickReadResponse: "Loading...", // Placeholder
          chatHistory: this.conversationHistory || [],
          isKorean: false,
        }).catch(() => null) // Don't fail if this fails
      ]);

      this.quickReadResponse = compatibilityResult.data.quickRead;

      // Cache pre-generated nudge question
      if (nudgeQuestionResult?.data?.nudgeQuestion) {
        this.preGeneratedNudgeQuestion = nudgeQuestionResult.data.nudgeQuestion;
      }

      this.currentInteraction = "postQuickRead";
      return {
        screenName: SCREENS.CHAT,
        chat: {
          text: this.quickReadResponse,
          allowResponse: false,
        },
      };
    }
  }

  async getBirthChart(userInfo) {
    // Check if running in simulator
    if (isSimulator()) {
      return SIMULATOR_RESPONSES.getBirthChart(userInfo?.name);
    }

    try {
      const result = await httpsCallable(
        functions,
        "submitBirthDetails"
      )({
        person1: {
          name: userInfo.name,
          date: userInfo.birthDate.toFormat("yyyy-MM-dd"),
          time: userInfo.birthDate.toFormat("HH:mm"),
          timeUnknown: userInfo.unknownTime || false,
          location: `${userInfo.birthLocation.latitude},${userInfo.birthLocation.longitude}`,
          gender: userInfo.gender,
          timezone: userInfo.birthLocation.timezone,
        },
        person2: {
          name: "System",
          date: "2000-01-01",
          time: "12:00",
          location: "0,0",
          gender: "O",
          timezone: userInfo.birthLocation.timezone,
        },
      });

      return result.data.charts.person1;
    } catch (error) {
      console.error('❌ Error in getBirthChart:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        stack: error.stack
      });

      // For simulator testing, return a mock chart to prevent stall
      return {
        name: userInfo.name,
        gender: userInfo.gender,
        // Add minimal chart data to prevent further errors
        saju: { elements: [] },
        western: { planets: [] }
      };
    }
  }

  async generateCardReadingScreen() {
    this.currentInteraction = "cardReading";

    // Check if running in simulator
    if (isSimulator()) {
      return {
        screenName: SCREENS.CARD_READING,
        cardReading: {
          text: SIMULATOR_RESPONSES.cardReading.text,
          card: SIMULATOR_RESPONSES.cardReading.card,
        },
      };
    }

    let functionName, data;
    // Normalize topic for backend guideline lookup (handles ... vs …)
    const normalizedTopic = (this.chosenTopic || "").replace("...", "…");
    if (this.mode == SELF_MODE) {
      functionName = "generateWeekAheadReading";
      data = {
        isKorean: false,
        birthChart: {
          person1: {
            ...this.state.birthChart,
            name: this.state.userInfo.name,
            gender: this.state.userInfo.gender,
          },
        },
      };
    } else {
      // mode == PAIR_MODE
      functionName = "generateCompatibilityImageResponse";
      data = {
        userChoice: "show me what's ahead",
        chosenTopic: normalizedTopic,
        quickReadResponse: this.quickReadResponse,
        nudgeQuestion: this.nudgeQuestion,
        extendedNudgeResponse: this.extendedNudgeResponse || "",
        chatHistory: [],
        isKorean: false,
        birthChart: {
          person1: {
            ...this.state.birthChart,
            name: this.state.userInfo.name,
            gender: this.state.userInfo.gender,
          },
          person2: this.state.otherPeople[this.otherPersonIndex],
        },
      };
    }
    const result = await this.callFunction(functionName, data);
    this.cardReadingText = result.data.imageResponse;
    return {
      screenName: SCREENS.CARD_READING,
      cardReading: {
        text: result.data.imageResponse,
        card: result.data.imageFile,
      },
    };
  }

  async generateContinuousChatScreen(message) {
    this.currentInteraction = "chat";

    // Check if running in simulator
    if (isSimulator()) {

      // Get hardcoded responses
      const responses = SIMULATOR_RESPONSES.getChatResponses(this.state.userInfo?.name);

      // Find matching response or use default
      const lowerMessage = message.toLowerCase();
      let response = responses.default;

      if (lowerMessage.includes('anxious') || lowerMessage.includes('anxiety')) {
        response = responses["i'm feeling anxious"];
      } else if (lowerMessage.includes('dating') || lowerMessage.includes('same type') || lowerMessage.includes('person')) {
        response = responses["i keep dating the same type of person"];
      } else if (lowerMessage.includes('work') || lowerMessage.includes('stressing')) {
        response = responses["work is stressing me out"];
      } else if (lowerMessage.includes('don\'t know') || lowerMessage.includes('want')) {
        response = responses["i don't know what i want"];
      } else if (lowerMessage.includes('self-sabotage') || lowerMessage.includes('sabotaging')) {
        response = responses["why do i keep self-sabotaging"];
      } else if (lowerMessage.includes('stuck')) {
        response = responses["i feel stuck"];
      }

      // Add to conversation history
      this.conversationHistory.push({
        isUser: true,
        text: message,
      });
      this.conversationHistory.push({
        isUser: false,
        text: response,
      });

      return {
        screenName: SCREENS.CHAT,
        chat: {
          text: response,
          allowResponse: true,
        },
      };
    }

    let data = {
      message,
      birthChart: {
        person1: {
          ...this.state.birthChart,
          name: this.state.userInfo.name,
          gender: this.state.userInfo.gender,
        },
        person2: {
          ...this.state.birthChart,
          name: this.state.userInfo.name,
          gender: this.state.userInfo.gender,
        },
      },
      isFirstMessage: false,
      conversationHistory: this.conversationHistory,
      userId: null,
      person1Id: null,
      person2Id: null,
      isRedFlagAnalysis: this.mode == SELF_MODE,
      priorContext: {
        chosenTopic: this.chosenTopic || null,
        quickRead: this.quickReadResponse || null,
        nudgeResponse: this.nudgeResponse || null,
        extendedNudge: this.extendedNudgeResponse || null,
        cardReading: this.cardReadingText || null,
      },
    };
    if (this.mode == PAIR_MODE) {
      data.birthChart.person2 = this.state.otherPeople[this.otherPersonIndex];
    }
    const result = await this.callFunction("chat", data);
    this.conversationHistory.push({
      isUser: true,
      text: message,
    });
    this.conversationHistory.push({
      isUser: false,
      text: result.data.response,
    });

    // Fire and forget — synthesize memory without blocking the UI
    this._synthesizeMemory();

    return {
      screenName: SCREENS.CHAT,
      chat: {
        text: result.data.response,
        allowResponse: true,
      },
    };
  }

  /**
   * Determines the next screen based on interaction data
   * @param {RequestData} data - The interaction data
   * @returns {Promise<ScreenSpecification>} The next screen configuration
   */
  async getNextScreen(data) {
    // This is the first time user flow.
    if (!("userInfo" in this.state)) {
      if (data.interactionType == INTERACTION_TYPES.OPEN_APP) {
        return {
          screenName: SCREENS.WOOJU_MACHINE,
          woojuMachine: {
            bubbleText:
              "Ask me anything—Wooju's listening 👁️🪐 Just need your birth dates.",
            floatingButtons: ["Get Started"],
          },
        };
      } else if (data.interactionType == INTERACTION_TYPES.FLOATING_BUTTONS) {
        return {
          screenName: SCREENS.INFO_CARD,
        };
      } else if (data.interactionType == INTERACTION_TYPES.INFO_CARD_SUBMIT) {
        this.state.userInfo = data.data;
        this.state.birthChart = await this.getBirthChart(data.data);
        // Save to localStorage
        this.saveToLocalStorage();
      }
    }

    if (this.currentInteraction == "chat") {
      if (
        data.interactionType == INTERACTION_TYPES.CHAT_CONTINUE ||
        (this.voiceMode && data.interactionType == INTERACTION_TYPES.FLOATING_BUTTONS)
      ) {
        return this.generateContinuousChatScreen(data.data);
      } else {
        console.error("Should not be here.");
      }
    }

    if (
      this.currentInteraction == "readOtherPerson" &&
      data.interactionType == INTERACTION_TYPES.INFO_CARD_SUBMIT
    ) {
      if (!this.state.otherPeople) {
        this.state.otherPeople = [];
      }
      const userInfo = data.data;
      Object.assign(userInfo, await this.getBirthChart(userInfo));
      this.otherPersonIndex = this.state.otherPeople.length;
      this.state.otherPeople.push(userInfo);
      // Save to localStorage
      this.saveToLocalStorage();
    }

    // Only proceed to WOOJU_MACHINE after INFO_CARD_SUBMIT
    if (data.interactionType == INTERACTION_TYPES.INFO_CARD_SUBMIT) {
      this.currentInteraction = "quickRead";
      const topicButtons = this.mode == SELF_MODE
        ? ["love & connections", "work & purpose", "just... life things"]
        : [
          "someone I'm crushing on",
          "someone I'm dating",
          "someone I used to love",
        ];
      // Wait for prefetch to finish in case InfoCard was submitted before it resolved
      if (this._memoryPrefetchPromise) {
        await this._memoryPrefetchPromise;
        this._memoryPrefetchPromise = null;
      }
      const bubbleText = this.mode == SELF_MODE
        ? (this.personalizedWelcome || "what's on your mind today?")
        : "so who's this person?";
      return {
        screenName: SCREENS.WOOJU_MACHINE,
        woojuMachine: {
          bubbleText,
          floatingButtons: topicButtons,
        },
      };
    }

    if (data.interactionType == INTERACTION_TYPES.FLOATING_BUTTONS) {
      if (this.currentInteraction == "quickRead") {
        return this.generateQuickReadScreen(data.data);
      }
      // Voice mode: after quick read, any speech goes directly to open chat
      if (this.currentInteraction == "postQuickRead" && this.voiceMode) {
        this.currentInteraction = "chat";
        return this.generateContinuousChatScreen(data.data);
      }
      if (this.currentInteraction == "nudge") {
        this.nudgeResponse = (data.data || "").toString().trim().toLowerCase();

        // Check if running in simulator
        if (isSimulator()) {
          this.extendedNudgeResponse = SIMULATOR_RESPONSES.getExtendedNudge(this.state.userInfo?.name);
          this.currentInteraction = "extendedNudgeChoices";
          return {
            screenName: SCREENS.WOOJU_MACHINE,
            woojuMachine: {
              bubbleText: this.extendedNudgeResponse,
              floatingButtons: ["show me what's ahead", "i just wanna chat"],
              // Hint to render larger bubble for long extended nudge
              largeSpeechBubble: true,
            },
          };
        }

        try {
          // Check if we have a pre-generated response for "yes" (most common)
          if (this.nudgeResponse === "yes" && this.preGeneratedExtendedNudge) {
            this.extendedNudgeResponse = this.preGeneratedExtendedNudge;
          } else {
            // Generate custom response for other choices
            const [extendedResult] = await Promise.all([
              this.callFunction("generateExtendedNudge", {
                userChoice: this.nudgeResponse.replace("...", ""),
                chosenTopic: this.chosenTopic,
                quickReadResponse: this.quickReadResponse,
                nudgeQuestion: this.nudgeQuestion,
                chatHistory: this.conversationHistory || [],
                birthChart: { person1: { ...this.state.birthChart, name: this.state.userInfo?.name, gender: this.state.userInfo?.gender } },
                isKorean: false,
              })
            ]);

            this.extendedNudgeResponse = extendedResult.data.extendedNudge;
          }
          this.currentInteraction = "extendedNudgeChoices";
          return {
            screenName: SCREENS.WOOJU_MACHINE,
            woojuMachine: {
              bubbleText: this.extendedNudgeResponse,
              floatingButtons: ["show me what's ahead", "i just wanna chat"],
              // Hint to render larger bubble for long extended nudge
              largeSpeechBubble: true,
            },
          };
        } catch (e) {
          console.error("Failed to generate extended nudge:", e);
          // Fallback to the simple question
          this.currentInteraction = "askIfUserWantsCardReading";
          return {
            screenName: SCREENS.WOOJU_MACHINE,
            woojuMachine: {
              bubbleText:
                "Would you like a card reading to see what's ahead for this week?",
              floatingButtons: ["yes pls", "no, just wanna chat"],
            },
          };
        }
      }
      if (this.currentInteraction == "extendedNudgeChoices") {
        const choice = (data.data || "").toString().trim().toLowerCase();

        // Check if running in simulator
        if (isSimulator()) {
          if (choice.startsWith("show me")) {
            this.currentInteraction = "cardReading";
            return this.generateCardReadingScreen();
          } else {
            // i just wanna chat
            this.currentInteraction = "chat";
            return {
              screenName: SCREENS.CHAT,
              chat: {
                text: SIMULATOR_RESPONSES.getChatResponse(this.state.userInfo?.name),
                allowResponse: true,
              },
            };
          }
        }

        if (choice.startsWith("show me")) {
          this.currentInteraction = "cardReading";
          return this.generateCardReadingScreen();
        } else {
          // i just wanna chat -> optional nodding response starter
          try {
            // Use Promise.all for potential parallelization
            const [nodResult] = await Promise.all([
              this.callFunction("generateNoddingResponse", {
                userChoice: "i just wanna chat",
                chosenTopic: this.chosenTopic,
                quickReadResponse: this.quickReadResponse,
                nudgeQuestion: this.nudgeQuestion,
                extendedNudgeResponse: this.extendedNudgeResponse || "",
                chatHistory: this.conversationHistory || [],
                isKorean: false,
              })
            ]);

            this.currentInteraction = "chat";
            this.noddingText = nodResult.data.noddingResponse;
            // Seed nodding response as first chat turn so the AI remembers it
            this.conversationHistory.push({
              isUser: false,
              text: this.noddingText,
            });
            return {
              screenName: SCREENS.CHAT,
              chat: {
                text: this.noddingText,
                allowResponse: true,
              },
            };
          } catch (e) {
            // Fallback: open empty chat
            this.currentInteraction = "chat";
            return {
              screenName: SCREENS.CHAT,
              chat: { text: "", allowResponse: true },
            };
          }
        }
      }
      if (this.currentInteraction == "postCardPrompt") {
        this.currentInteraction = "chat";
        // Open chat with input enabled and no model message
        return {
          screenName: SCREENS.CHAT,
          chat: {
            text: "",
            allowResponse: true,
          },
        };
      }
      if (this.currentInteraction == "askIfUserWantsCardReading") {
        const reply = (data.data || "").toString().trim().toLowerCase();
        if (reply === "yes" || reply.startsWith("yes ") || reply.startsWith("yes")) {
          this.currentInteraction = "cardReading";
          return this.generateCardReadingScreen();
        } else {
          // User doesn't want card reading: go straight to chat with no auto message
          this.currentInteraction = "chat";
          return {
            screenName: SCREENS.CHAT,
            chat: {
              text: "",
              allowResponse: true,
            },
          };
        }
      }
      if (this.currentInteraction == "readOtherPerson") {
        if (data.data == "yes") {
          this.mode = PAIR_MODE;
          return {
            screenName: SCREENS.INFO_CARD,
            infoCard: { requireBlank: true },
          };
        } else {
          this.currentInteraction = "nudge";
          return this.generateNudgeQuestionScreen();
        }
      }
    }

    if (data.interactionType == INTERACTION_TYPES.CHAT_CONTINUE) {
      if (this.currentInteraction == "postQuickRead") {
        // Voice mode: skip all gates, go straight to open chat
        if (this.voiceMode) {
          this.currentInteraction = "chat";
          return this.generateContinuousChatScreen(data.data || "");
        }
        // Text mode: existing gate behaviour
        if (this.chosenTopic == "love & connections" && this.mode == SELF_MODE) {
          this.currentInteraction = "readOtherPerson";
          return {
            screenName: SCREENS.WOOJU_MACHINE,
            woojuMachine: {
              bubbleText: "Do you have someone in mind?",
              floatingButtons: ["yes", "no"],
            },
          };
        }
        return this.generateNudgeQuestionScreen();
      } else if (this.currentInteraction == "cardReading") {
        // Show Zoltar again with a prompt before entering chat
        this.currentInteraction = "postCardPrompt";
        return {
          screenName: SCREENS.WOOJU_MACHINE,
          woojuMachine: {
            bubbleText: "anything else you want to know?",
            floatingButtons: [],
          },
        };
      } else if (this.currentInteraction == "postCardPrompt") {
        // User typed under the lady prompt: go straight into chat with their message
        return this.generateContinuousChatScreen(data.data || "");
      } else {
        return this.generateNudgeQuestionScreen();
      }
    }

    return {};
  }
}
